import { Component, ComponentInterface, Element, h, Host, Prop, Watch } from '@stencil/core';
import { ButtonColorOptions, ExpandOptions, PredefinedColors, SizeOptions } from '../../interface';

/**
 * Figma design link
 * @figma https://www.figma.com/design/ngaGX6aSwvCpJxDwjhd38h/Input-and-button?node-id=145-3338&m=dev
 */

@Component({
    tag: 'rs-button',
    styleUrl: 'button.css',
    shadow: true
})

export class Button implements ComponentInterface {
    @Element() el: HTMLElement;

    /** (optional) primary, secondary, positive, grey defaults to primary. If set to grey, button will be an outline */
    @Prop() color: ButtonColorOptions = 'primary';

    /** (optional) Puts the button in a disabled state */
    @Prop() disabled: boolean = false;

    /**
     * If specified, the button will be an "a" tag with a href attribute
     */
    @Prop() href: string | undefined;

    /**
     * Rel string that goes with a normal a tag
     */
    @Prop() rel: string | undefined;

    /**
     * window target for a tag type buttons
     */
    @Prop() target: string | undefined;

    /**
     * (optional) Changes the button the small variation
     *
     * @property small Button with less height padding, and smaller font size
     * @property default Button with the default height and padding
     */
    @Prop() size: SizeOptions = 'default';


    /**
     * If true, replaces the inner html to a loading bar
     */
    @Prop() loading: boolean = false;

    /**
     * (optional) This attribute lets you specify how wide the button should be. By default, buttons are inline blocks, but setting this attribute will change the button to a full-width block element
     *
     * @property expand Block = Full-width button with rounded corners. full Full-width button with square corners. collapse No defined width, or height. Width defined by the width attribute, with an aspect ratio of 1
     */
    @Prop() expand: ExpandOptions;

    /**
     * If expand=collapse, this attribute will set the width of the button with an aspect-ratio of 1
     */
    @Prop() width: number;

    /**
     * (optional) An icon to display at the start of the button text
     */
    @Prop() iconStart: string;

    /**
     * (optional) An icon to display at the end of the button text
     */
    @Prop() iconEnd: string;

    /**
     * (optional) An icon to display at the end of the button text
     */
    @Prop() iconSize: number;

    /**
     * (optional) An icon to display at the end of the button text
     */
    @Prop() iconColor: PredefinedColors;

    /**
     * flex-grow css rule
     */
    @Prop() flexGrow: string;
    private buttonElement: HTMLElement;

    private getColorClass() {
        return `color--${ this.color }`;
    }

    private getExpandClass() {
        if (this.expand === 'block') {
            return 'expand-block';
        } else if (this.expand === 'full') {
            return 'expand-full';
        }

        return '';
    }

    @Watch('loading')
    setButtonWidth() {
        if (this.loading) {
            this.buttonElement.style.width = `${ this.buttonElement.offsetWidth }px`;
        } else {
            this.buttonElement.style.width = null;
        }
    }

    render() {
        const { href, rel, target } = this;
        const TagType = this.href === undefined ? 'button' : 'a';
        const attrs = (TagType === 'a')
            ? {
                href,
                rel,
                target
            } : {};

        return (
            <Host class={ this.getExpandClass() } style={ {
                'flex-grow': this.flexGrow
            } }>
                <TagType
                    ref={ (el: HTMLElement) => this.buttonElement = el }
                    { ...attrs }
                    disabled={ this.disabled }
                    class={ [
                        'button-element',
                        this.loading ? 'button-element--loading' : '',
                        this.getColorClass(),
                        this.size === 'small' ? 'small' : '',
                        this.disabled ? 'disabled' : '',
                        this.expand !== undefined ? `button-element--${ this.expand }` : ''
                    ].join(' ').trim() }
                    style={ {
                        width: this.expand === 'collapse' ? `${ this.width }px` : null,
                    } }
                >
                    { this.renderStartIcon() }
                    { this.slot() }
                    { this.renderEndIcon() }
                </TagType>
            </Host>
        );
    }

    private slot() {
        if (!this.el.textContent) {
            return null;
        }

        if (this.loading) {
            // return (<span><div class="button-element__loading-bar"/></span>);
            return (
                <rs-loader type="indeterminate"></rs-loader>
            );
        }

        return (
            <span><slot/></span>
        );
    }

    private renderStartIcon() {
        return (
            this.iconStart !== undefined ? <rs-icon size={ this.iconSize } color={ this.iconColor } class="icon-start"
                                                    name={ this.iconStart }/> : null
        );
    }

    private renderEndIcon() {
        return (
            this.iconEnd !== undefined ?
                <rs-icon size={ this.iconSize } color={ this.iconColor } class="icon-end" name={ this.iconEnd }/> : null
        );
    }
}